import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import {
  AlertTriangle, Plus, X, Filter, Search, Clock, CheckCircle,
  Eye, MessageSquare, Camera, MapPin, DollarSign, Calendar,
  User, ChevronRight, Shield, AlertCircle, FileWarning
} from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_FLOW = {
  open: { color: '#EF4444', label: 'Abierto', next: 'investigating' },
  investigating: { color: '#F59E0B', label: 'Investigando', next: 'corrective_action' },
  corrective_action: { color: '#3B82F6', label: 'Acción correctiva', next: 'verification' },
  verification: { color: '#8B5CF6', label: 'Verificación', next: 'closed' },
  closed: { color: '#10B981', label: 'Cerrado', next: null },
  cancelled: { color: '#64748B', label: 'Cancelado', next: null },
};

const SEVERITY_CONFIG = {
  critical: { color: '#EF4444', label: 'Crítico', bg: 'rgba(239,68,68,0.15)' },
  major: { color: '#F59E0B', label: 'Mayor', bg: 'rgba(245,158,11,0.15)' },
  minor: { color: '#3B82F6', label: 'Menor', bg: 'rgba(59,130,246,0.15)' },
  observation: { color: '#64748B', label: 'Observación', bg: 'rgba(100,116,139,0.15)' },
};

const TYPE_LABELS = { ncr: 'NCR', incident: 'Incidencia', observation: 'Observación', improvement: 'Mejora' };

export default function Incidents() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [incidents, setIncidents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIncident, setActiveIncident] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [users, setUsers] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [newComment, setNewComment] = useState('');
  const [createForm, setCreateForm] = useState({
    type: 'ncr', severity: 'minor', title: '', description: '',
    locationZone: '', assignedTo: '', deadlineDate: '',
  });

  const canEdit = ['superadmin', 'admin_empresa', 'project_manager', 'supervisor'].includes(user?.role);

  useEffect(() => {
    Promise.all([
      api.get('/projects').then(r => setProjects(r.data.data || r.data || [])),
      api.get('/users', { params: { limit: 200 } }).then(r => setUsers(r.data.data || r.data || [])),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadIncidents(); }, [selectedProject, filterStatus, filterSeverity]);

  const loadIncidents = () => {
    const params = {};
    if (selectedProject) params.project_id = selectedProject;
    if (filterStatus) params.status = filterStatus;
    if (filterSeverity) params.severity = filterSeverity;
    api.get('/incidents', { params }).then(r => setIncidents(r.data)).catch(() => {});
    api.get('/incidents/stats/summary', { params: selectedProject ? { project_id: selectedProject } : {} })
      .then(r => setStats(r.data)).catch(() => {});
  };

  const loadDetail = async (id) => {
    try {
      const { data } = await api.get(`/incidents/${id}`);
      setActiveIncident(data);
    } catch (e) { toast.error('Error'); }
  };

  const createIncident = async () => {
    if (!selectedProject) return toast.error('Selecciona un proyecto');
    if (!createForm.title) return toast.error('Título requerido');
    if (!createForm.description) return toast.error('Descripción requerida');
    try {
      await api.post('/incidents', { ...createForm, projectId: selectedProject });
      toast.success('Incidencia creada');
      setShowCreate(false);
      setCreateForm({ type: 'ncr', severity: 'minor', title: '', description: '', locationZone: '', assignedTo: '', deadlineDate: '' });
      loadIncidents();
    } catch (e) { toast.error('Error'); }
  };

  const advanceStatus = async () => {
    if (!activeIncident) return;
    const nextStatus = STATUS_FLOW[activeIncident.status]?.next;
    if (!nextStatus) return;
    try {
      const { data } = await api.put(`/incidents/${activeIncident.id}`, { status: nextStatus });
      setActiveIncident(prev => ({ ...prev, ...data, status: nextStatus }));
      loadDetail(activeIncident.id);
      loadIncidents();
      toast.success(`Estado: ${STATUS_FLOW[nextStatus]?.label}`);
    } catch (e) { toast.error('Error'); }
  };

  const updateField = async (field, value) => {
    try {
      await api.put(`/incidents/${activeIncident.id}`, { [field]: value });
      setActiveIncident(prev => ({ ...prev, [field]: value }));
    } catch (e) { toast.error('Error'); }
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    try {
      await api.post(`/incidents/${activeIncident.id}/comment`, { comment: newComment });
      setNewComment('');
      loadDetail(activeIncident.id);
    } catch (e) { toast.error('Error'); }
  };

  return (
    <div className="incidents-page">
      <div className="page-header">
        <div>
          <h1><AlertTriangle size={24} /> Incidencias / NCR</h1>
          <p className="subtitle">Registro de no conformidades y seguimiento</p>
        </div>
        {canEdit && (
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            <Plus size={16} /> Nueva Incidencia
          </button>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div className="inc-stats">
          <div className="inc-stat"><span className="inc-stat-value" style={{ color: '#EF4444' }}>{stats.open || 0}</span><span>Abiertas</span></div>
          <div className="inc-stat"><span className="inc-stat-value" style={{ color: '#F59E0B' }}>{stats.investigating || 0}</span><span>Investigando</span></div>
          <div className="inc-stat"><span className="inc-stat-value" style={{ color: '#3B82F6' }}>{stats.corrective_action || 0}</span><span>Correctiva</span></div>
          <div className="inc-stat"><span className="inc-stat-value" style={{ color: '#10B981' }}>{stats.closed || 0}</span><span>Cerradas</span></div>
          <div className="inc-stat"><span className="inc-stat-value" style={{ color: '#EF4444' }}>{stats.critical || 0}</span><span>Críticas</span></div>
          {parseFloat(stats.total_cost_impact) > 0 && (
            <div className="inc-stat"><span className="inc-stat-value" style={{ color: '#F59E0B' }}>{Number(stats.total_cost_impact).toLocaleString()}€</span><span>Impacto €</span></div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="filters-bar">
        <select className="filter-select" value={selectedProject} onChange={e => setSelectedProject(e.target.value)}>
          <option value="">Todos los proyectos</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
        </select>
        <select className="filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">Todos los estados</option>
          {Object.entries(STATUS_FLOW).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select className="filter-select" value={filterSeverity} onChange={e => setFilterSeverity(e.target.value)}>
          <option value="">Todas las severidades</option>
          {Object.entries(SEVERITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      <div className="incidents-layout">
        {/* List */}
        <div className="incidents-list">
          {incidents.length === 0 && !loading && (
            <div className="empty-state"><AlertTriangle size={48} /><h3>Sin incidencias</h3></div>
          )}
          {incidents.map(inc => {
            const sev = SEVERITY_CONFIG[inc.severity] || SEVERITY_CONFIG.minor;
            const st = STATUS_FLOW[inc.status] || STATUS_FLOW.open;
            return (
              <div key={inc.id}
                className={`inc-card ${activeIncident?.id === inc.id ? 'active' : ''}`}
                onClick={() => loadDetail(inc.id)}>
                <div className="inc-card-top">
                  <span className="inc-code">{inc.code}</span>
                  <span className="inc-severity-badge" style={{ background: sev.bg, color: sev.color }}>{sev.label}</span>
                  <span className="inc-status-dot" style={{ background: st.color }} title={st.label} />
                </div>
                <h4 className="inc-card-title">{inc.title}</h4>
                <div className="inc-card-meta">
                  <span>{inc.project_code}</span>
                  <span>{TYPE_LABELS[inc.type]}</span>
                  {inc.assigned_to_name && <span><User size={10} /> {inc.assigned_to_name}</span>}
                  <span>{new Date(inc.created_at).toLocaleDateString('es')}</span>
                </div>
                <div className="inc-card-counts">
                  {parseInt(inc.photo_count) > 0 && <span><Camera size={10} /> {inc.photo_count}</span>}
                  {parseInt(inc.comment_count) > 0 && <span><MessageSquare size={10} /> {inc.comment_count}</span>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Detail */}
        <div className="incident-detail">
          {!activeIncident ? (
            <div className="empty-state"><Eye size={48} /><p>Selecciona una incidencia</p></div>
          ) : (
            <div className="inc-detail-content">
              <div className="inc-detail-header">
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span className="inc-code-lg">{activeIncident.code}</span>
                    <span className="inc-severity-badge"
                      style={{ background: SEVERITY_CONFIG[activeIncident.severity]?.bg, color: SEVERITY_CONFIG[activeIncident.severity]?.color }}>
                      {SEVERITY_CONFIG[activeIncident.severity]?.label}
                    </span>
                    <span className="cl-badge" style={{
                      background: STATUS_FLOW[activeIncident.status]?.color + '20',
                      color: STATUS_FLOW[activeIncident.status]?.color
                    }}>{STATUS_FLOW[activeIncident.status]?.label}</span>
                  </div>
                  <h2>{activeIncident.title}</h2>
                </div>
                {canEdit && STATUS_FLOW[activeIncident.status]?.next && (
                  <button className="btn btn-sm btn-primary" onClick={advanceStatus}>
                    <ChevronRight size={14} /> {STATUS_FLOW[STATUS_FLOW[activeIncident.status].next]?.label}
                  </button>
                )}
              </div>

              {/* Description */}
              <div className="inc-section">
                <h4>Descripción</h4>
                <p>{activeIncident.description}</p>
              </div>

              {/* Info grid */}
              <div className="inc-info-grid">
                <div><label>Tipo</label><span>{TYPE_LABELS[activeIncident.type]}</span></div>
                <div><label>Reportado por</label><span>{activeIncident.reported_by_name}</span></div>
                <div><label>Asignado a</label><span>{activeIncident.assigned_to_name || '—'}</span></div>
                <div><label>Fecha</label><span>{new Date(activeIncident.created_at).toLocaleDateString('es')}</span></div>
                {activeIncident.location_zone && <div><label>Zona</label><span>{activeIncident.location_zone}</span></div>}
                {activeIncident.deadline_date && <div><label>Plazo</label><span>{new Date(activeIncident.deadline_date).toLocaleDateString('es')}</span></div>}
                {parseFloat(activeIncident.cost_impact) > 0 && <div><label>Impacto €</label><span style={{ color: '#EF4444' }}>{Number(activeIncident.cost_impact).toLocaleString()}€</span></div>}
                {activeIncident.schedule_impact_days > 0 && <div><label>Impacto días</label><span style={{ color: '#F59E0B' }}>{activeIncident.schedule_impact_days} días</span></div>}
              </div>

              {/* Actions fields */}
              {canEdit && (
                <div className="inc-actions-section">
                  <div className="form-group">
                    <label>Causa raíz</label>
                    <textarea rows={2} value={activeIncident.root_cause || ''}
                      onChange={e => setActiveIncident(prev => ({ ...prev, root_cause: e.target.value }))}
                      onBlur={e => updateField('rootCause', e.target.value)}
                      placeholder="Análisis de causa raíz..." />
                  </div>
                  <div className="form-group">
                    <label>Acción correctiva</label>
                    <textarea rows={2} value={activeIncident.corrective_action || ''}
                      onChange={e => setActiveIncident(prev => ({ ...prev, corrective_action: e.target.value }))}
                      onBlur={e => updateField('correctiveAction', e.target.value)}
                      placeholder="Acción correctiva propuesta..." />
                  </div>
                  <div className="form-group">
                    <label>Acción preventiva</label>
                    <textarea rows={2} value={activeIncident.preventive_action || ''}
                      onChange={e => setActiveIncident(prev => ({ ...prev, preventive_action: e.target.value }))}
                      onBlur={e => updateField('preventiveAction', e.target.value)}
                      placeholder="Medida preventiva..." />
                  </div>
                </div>
              )}

              {/* Photos */}
              {activeIncident.photos?.length > 0 && (
                <div className="inc-section">
                  <h4><Camera size={14} /> Fotos ({activeIncident.photos.length})</h4>
                  <div className="inc-photos-grid">
                    {activeIncident.photos.map(p => (
                      <div key={p.id} className="inc-photo">
                        <img src={p.file_url} alt={p.description || ''} loading="lazy" />
                        <span className="inc-photo-type">{p.photo_type}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Timeline */}
              <div className="inc-section">
                <h4><Clock size={14} /> Timeline ({(activeIncident.comments || []).length})</h4>
                <div className="inc-timeline">
                  {(activeIncident.comments || []).map(c => (
                    <div key={c.id} className={`inc-comment ${c.is_status_change ? 'status-change' : ''}`}>
                      <div className="inc-comment-header">
                        <span className="inc-comment-user">{c.user_name}</span>
                        <span className="inc-comment-date">{new Date(c.created_at).toLocaleString('es')}</span>
                      </div>
                      <p>{c.comment}</p>
                    </div>
                  ))}
                </div>
                {canEdit && (
                  <div className="inc-add-comment">
                    <input value={newComment} onChange={e => setNewComment(e.target.value)}
                      placeholder="Añadir comentario..." onKeyDown={e => e.key === 'Enter' && addComment()} />
                    <button className="btn btn-sm btn-primary" onClick={addComment}>Enviar</button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Nueva Incidencia / NCR</h3>
              <button className="btn-icon" onClick={() => setShowCreate(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Proyecto *</label>
                  <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)}>
                    <option value="">Seleccionar...</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Tipo</label>
                  <select value={createForm.type} onChange={e => setCreateForm(f => ({ ...f, type: e.target.value }))}>
                    {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Severidad</label>
                  <select value={createForm.severity} onChange={e => setCreateForm(f => ({ ...f, severity: e.target.value }))}>
                    {Object.entries(SEVERITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Asignar a</label>
                  <select value={createForm.assignedTo} onChange={e => setCreateForm(f => ({ ...f, assignedTo: e.target.value }))}>
                    <option value="">Sin asignar</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Título *</label>
                <input value={createForm.title} onChange={e => setCreateForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Título descriptivo de la incidencia" />
              </div>
              <div className="form-group">
                <label>Descripción *</label>
                <textarea rows={4} value={createForm.description}
                  onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Describe el problema, ubicación, condiciones..." />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Zona / Ubicación</label>
                  <input value={createForm.locationZone} onChange={e => setCreateForm(f => ({ ...f, locationZone: e.target.value }))}
                    placeholder="Ej: Zona A, String 3" />
                </div>
                <div className="form-group">
                  <label>Fecha límite</label>
                  <input type="date" value={createForm.deadlineDate}
                    onChange={e => setCreateForm(f => ({ ...f, deadlineDate: e.target.value }))} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={createIncident}>Crear Incidencia</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
